import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

const CANDLE_COUNT = 42

// A drifting price path, fixed so every render draws the same chart.
const CANDLES = Array.from({ length: CANDLE_COUNT }, (_, i) => {
  const center = 50 - Math.sin(i / 6) * 18 - Math.sin(i / 2.3) * 4
  const body = 4 + ((i * 7) % 5) * 2
  const wick = body + 4 + ((i * 5) % 4) * 2
  return { center, body, wick, volume: 20 + ((i * 37) % 70) }
})

const PRICE_LABELS = 7
const TIME_LABELS = 5
const DRAWING_TOOLS = 9

const Divider = () => <span className="bg-foreground/10 h-4 w-px shrink-0" />

/**
 * Loading stand-in shaped like the chart widget, which both engines (TradingView and Percept)
 * lay out the same way: the header toolbar (symbol, timeframes, chart type, Indicators,
 * Compare, fullscreen), the drawing rail, the legend with Sell/Buy and volume, candles over a
 * volume strip, the price and time axes, and the footer (market status, clock, scale modes).
 */
export function ChartSkeleton({ className }: { className?: string }) {
  return (
    <div aria-busy="true" className={cn('flex h-full w-full flex-col overflow-hidden', className)}>
      <div className="flex h-11 shrink-0 items-center gap-2.5 px-3">
        <Skeleton className="bg-foreground/10 h-4 w-20" />
        <Divider />
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="bg-foreground/10 h-5 w-7 rounded-md" />
        ))}
        <Skeleton className="bg-foreground/10 size-3" />
        <Divider />
        <Skeleton className="bg-foreground/10 size-5 rounded-md" />
        <Divider />
        <Skeleton className="bg-foreground/10 h-4 w-20 max-sm:hidden" />
        <Skeleton className="bg-foreground/10 h-4 w-16 max-sm:hidden" />
        <Skeleton className="bg-foreground/10 ml-auto size-5" />
      </div>

      <div className="flex min-h-0 flex-1">
        <div className="flex w-12 shrink-0 flex-col items-center gap-5 overflow-hidden pt-3 max-sm:hidden">
          {Array.from({ length: DRAWING_TOOLS }, (_, i) => (
            <Skeleton key={i} className="bg-foreground/10 size-5 shrink-0" />
          ))}
        </div>

        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex min-h-0 flex-1">
            <div className="relative min-w-0 flex-1">
              <div className="absolute top-2 left-2 z-10 flex flex-col gap-2">
                <Skeleton className="bg-foreground/10 h-3 w-64 max-w-[60vw]" />
                <div className="flex gap-2">
                  <Skeleton className="bg-foreground/10 h-7 w-14 rounded-md" />
                  <Skeleton className="bg-foreground/10 h-7 w-14 rounded-md" />
                </div>
                <Skeleton className="bg-foreground/10 h-3 w-24" />
              </div>

              <div className="absolute inset-x-2 top-[14%] bottom-[20%] flex gap-[3px]">
                {CANDLES.map((candle, i) => (
                  <div key={i} className="relative h-full min-w-0 flex-1">
                    <Skeleton
                      className="bg-foreground/8 absolute left-1/2 w-px -translate-x-1/2"
                      style={{
                        top: `${candle.center - candle.wick / 2}%`,
                        height: `${candle.wick}%`,
                      }}
                    />
                    <Skeleton
                      className="bg-foreground/10 absolute inset-x-0"
                      style={{
                        top: `${candle.center - candle.body / 2}%`,
                        height: `${candle.body}%`,
                      }}
                    />
                  </div>
                ))}
              </div>

              <div className="absolute inset-x-2 bottom-0 flex h-[16%] items-end gap-[3px]">
                {CANDLES.map((candle, i) => (
                  <Skeleton
                    key={i}
                    className="bg-foreground/6 min-w-0 flex-1"
                    style={{ height: `${candle.volume}%` }}
                  />
                ))}
              </div>
            </div>

            <div className="flex w-16 shrink-0 flex-col justify-between py-6 pr-3 pl-2">
              {Array.from({ length: PRICE_LABELS }, (_, i) => (
                <Skeleton key={i} className="bg-foreground/10 h-2.5 w-full" />
              ))}
            </div>
          </div>

          <div className="flex h-7 shrink-0 items-center justify-around pr-16">
            {Array.from({ length: TIME_LABELS }, (_, i) => (
              <Skeleton key={i} className="bg-foreground/10 h-2.5 w-8" />
            ))}
          </div>
        </div>
      </div>

      <div className="flex h-8 shrink-0 items-center gap-3 px-3">
        <Skeleton className="bg-foreground/10 h-4 w-28" />
        <Skeleton className="bg-foreground/10 ml-auto h-3 w-24 max-sm:hidden" />
        <Skeleton className="bg-foreground/10 h-3 w-16" />
      </div>
    </div>
  )
}
